import React, { useRef, useState } from "react";
import { staffApi, formatApiError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X } from "lucide-react";

/**
 * ImageUpload — uploads a file to the backend, which compresses to 500x500 JPEG
 * and returns a URL path. Calls onChange with the final URL string.
 */
export default function ImageUpload({ value, onChange, label = "Service image", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await staffApi.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.(r.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const src = value ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`) : "";

  return (
    <div className="flex items-start gap-4" data-testid={testid}>
      <div className="w-24 h-24 rounded-lg border border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden">
        {src ? (
          <img src={src} alt="preview" className="w-full h-full object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <ImageIcon className="w-8 h-8 text-muted-foreground" />
        )}
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium mb-1">{label}</p>
        <p className="text-xs text-muted-foreground mb-2">JPG or PNG, auto-cropped to 500×500.</p>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFile}
            data-testid={`${testid}-input`}
          />
          <Button type="button" variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`${testid}-btn`} className="gap-2">
            <Upload className="w-4 h-4" /> {busy ? "Uploading..." : (value ? "Replace" : "Upload")}
          </Button>
          {value && (
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange?.("")} data-testid={`${testid}-clear`} className="gap-1">
              <X className="w-4 h-4" /> Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
